import type { SubmissionContext } from 'src/pages/supplier/SubmitReport/types';
import type { SupplierContractsApplications } from 'src/pages/supplier/SubmitApplication/schema';

import { axios, formatError } from './utils';

export const createSupplier = async (data: {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  businessName: string;
  token: string;
}) => {
  try {
    const response = await axios.post('/api/supplier', data);
    return response.data;
  } catch (error) {
    throw formatError(error);
  }
};

export const getSupplierApplication = async (applicationId: number) => {
  try {
    const response = await axios.get(`/api/supplier/application/${applicationId}`);
    return response.data.data;
  } catch (error) {
    throw formatError(error);
  }
};

export const updateSupplierContractApplication = async (
  data: SupplierContractsApplications & {
    applicationId: number;
    isSubmit: boolean;
    sessionId: string;
  }
) => {
  try {
    const response = await axios.put(`/api/supplier/application/${data.applicationId}`, data);
    return response.data.data;
  } catch (error) {
    throw formatError(error);
  }
};

/**
 * Serves the supplier's own reporting context or, for the contract's procurer, the previous-delivery
 * context for the period named by `reportId`. Which one comes back is decided server-side from the
 * caller, so `isBackfill` is read from the response rather than inferred here.
 */
export const getSubmissionContext = async (
  contractId: number,
  isEdit?: boolean,
  reportId?: number
): Promise<SubmissionContext> => {
  try {
    const { data } = await axios.get(`/api/report/${contractId}/context`, {
      params: {
        isEdit,
        reportId,
      },
    });
    return data.data;
  } catch (error) {
    throw formatError(error);
  }
};

export type DraftPayload = {
  contractId: number; // injected by route param on server
  supplierContractApplicationId: number; // backend derives; we still include for DTO symmetry if needed
  reportActivities: any[];
};

export const saveDraft = async (contractId: number, body: DraftPayload) => {
  try {
    const { data } = await axios.post(`/api/report/${contractId}/draft`, body);
    return data;
  } catch (error) {
    throw formatError(error);
  }
};

export const submitReport = async (contractId: number, body: DraftPayload) => {
  try {
    const { data } = await axios.post(`/api/report/${contractId}/submit`, body);
    return data;
  } catch (error) {
    throw formatError(error);
  }
};

export const initEditReport = async (reportId: number) => {
  try {
    const { data } = await axios.post(`/api/report/${reportId}/init-edit-report`);
    return data;
  } catch (error) {
    throw formatError(error);
  }
};

export const initEditApplication = async (applicationId: number) => {
  try {
    const { data } = await axios.post(`/api/supplier/application/${applicationId}/init-edit`);
    return data;
  } catch (error) {
    throw formatError(error);
  }
};

// file name plus presigned URL only
export interface SupplierPrivacyPolicy {
  required: boolean;
  accepted: boolean;
  procurerName: string;
  privacyDocument: {
    originalName: string;
    downloadUrl: string;
  } | null;
}

export const getSupplierPrivacyPolicy = async (
  contractId: number
): Promise<SupplierPrivacyPolicy> => {
  try {
    const response = await axios.get(`/api/supplier/contract/${contractId}/privacy-policy`);
    return response.data.data;
  } catch (error) {
    throw formatError(error);
  }
};

export const acceptSupplierPrivacyPolicy = async (contractId: number) => {
  try {
    const response = await axios.post(`/api/supplier/contract/${contractId}/privacy-policy/accept`);
    return response.data.data;
  } catch (error) {
    throw formatError(error);
  }
};
